import styled from 'styled-components/macro'
import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import shadows from '@mui/material/styles/shadows'
import { Grid, Box, Typography } from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import CheckIcon from '@mui/icons-material/Check'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import { DatePicker, TimePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import format from 'date-fns/format'
import { formatDecimal } from '../../services/utils/format-numbers'
import { useTheme } from 'styled-components/macro'
import {
  StyledMenuButton,
  StyledButton,
  StyledDiv,
  StyledLabel,
  StyledTextField,
  StyledTimePicker,
  ButtonGroup,
  StyledText,
} from '../EnergyMix/style'
import {
  OuterSurface,
  BoxTypography,
  BoxData,
  BaseEnergyShareKpi,
  IconWrapper,
  EnergyMixButton,
  EnergyShareKpiTypography,
  EnergyShareKpiData,
  Container,
  EnergySummaryBox,
} from './styles'
import {
  BioMassIcon,
  WindEnergyIcon,
  HydroEnergyIcon,
  SolarEnergyIcon,
} from '../../components/icons/myIcons'
import { useNavigate } from 'react-router-dom'
import EnergyBarChart from './EnergyBarChart'
import Feedback from '../../components/Feedback'
import { Periods } from './Cockpit'

const HeaderTypography = styled(Typography)`
  && {
    color: ${(props) => props.theme.tokens.secondary};
    position: relative;
    margin: 10px;
  }
`

/**
 * This is the component to show main KPIs and the bar graph on dashboard.
 * It holds the KPIs i.e. related to production and consumption that are updated with date selection
 * on Uberblick Energie Page of dashboardand grpah.
 *
 * @returns {React.ReactNode} A React component that renders KPIs and also the Bar graph.
 */
function EnergyKpi({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  period,
  setPeriod,
  data,
}) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const [error, setError] = useState(false)
  const theme = useTheme()

  // Process the data to accumulate energy by type and contract type
  const processedData = useMemo(() => {
    if (!data?.months) return null

    const totals = {
      totalEnergyProduction: 0,
      totalDemand: 0,
      totalGridEnergy: 0,
      totalProductionByEnergySources: {
        BIO_MASS: 0,
        SOLAR_POWER: 0,
        WIND_ENERGY: 0,
        HYDRO_POWER: 0,
      },
    }

    // Process each month's data
    data.months.forEach((monthData) => {
      const {
        bioMassEnergy,
        solarEnergy,
        windEnergy,
        hydroEnergy,
        gridEnergy,
        totalEnergyValue,
      } = monthData

      // Accumulate production by energy source
      totals.totalProductionByEnergySources.BIO_MASS += bioMassEnergy || 0
      totals.totalProductionByEnergySources.SOLAR_POWER += solarEnergy || 0
      totals.totalProductionByEnergySources.WIND_ENERGY += windEnergy || 0
      totals.totalProductionByEnergySources.HYDRO_POWER += hydroEnergy || 0

      // Total production is sum of all renewable energy sources
      totals.totalEnergyProduction +=
        (bioMassEnergy || 0) +
        (solarEnergy || 0) +
        (windEnergy || 0) +
        (hydroEnergy || 0)

      // Grid energy represents demand
      totals.totalGridEnergy += gridEnergy || 0
      totals.totalDemand += totalEnergyValue || 0
    })

    return {
      data: totals,
    }
  }, [data])

  // Process the data for the energy bar chart (salePurchaseData)
  const salePurchaseData = useMemo(() => {
    if (!data?.months) return { data: { salesPurchasesDtoset: [] } }

    // Transform monthly data to the expected format
    const monthlyData = data.months.map((monthData) => {
      const {
        month,
        bioMassEnergy,
        solarEnergy,
        windEnergy,
        hydroEnergy,
        gridEnergy,
      } = monthData

      // Calculate total production (sold energy) from all renewable sources
      const totalProduction =
        (bioMassEnergy || 0) +
        (solarEnergy || 0) +
        (windEnergy || 0) +
        (hydroEnergy || 0)

      // Grid energy represents purchased energy
      const totalPurchased = gridEnergy || 0

      // Extract month name and year from ISO date string
      const monthName = dayjs(month).format('MMMM').toUpperCase()
      const year = dayjs(month).format('YYYY')

      return {
        time: monthName,
        year: year,
        soldEnergy: totalProduction,
        purchasedEnergy: totalPurchased,
      }
    })

    return {
      data: {
        salesPurchasesDtoset: monthlyData,
        totalSales: monthlyData.reduce(
          (sum, month) => sum + month.soldEnergy,
          0
        ),
        totalPurchase: monthlyData.reduce(
          (sum, month) => sum + month.purchasedEnergy,
          0
        ),
      },
    }
  }, [data])

  const handleButtonClick = (buttonType) => {
    setPeriod(buttonType)
  }

  const handleClick = () => {
    navigate('/energyMix')
  }
  const closeFeedback = () => {
    setError(false)
  }

  const initialValues = {
    startDate: startDate,
    endDate: endDate,
    startTime: (() => {
      const date = new Date()
      date.setHours(0, 0, 0, 0)
      return date
    })(),
    endTime: (() => {
      const date = new Date()
      date.setHours(23, 59, 0, 0)
      return date
    })(),
  }
  const validationSchema = useMemo(
    () =>
      Yup.object({
        startDate: Yup.date().required(i18n.t('LBLStartDateReq')),
        endDate: Yup.date().required(i18n.t('LBLEndDateReq')),
        startTime: Yup.date().required(i18n.t('LBLStartTimeReq')),
        endTime: Yup.date().required(i18n.t('LBLEndTimeReq')),
      }),
    [i18n]
  )
  //  This is the handle submit function that is triggered when the time selection is made on the dashboard
  const handleSubmit = (values, actions) => {
    const startDateTime = new Date(values.startDate)
    const endDateTime = new Date(values.endDate)
    startDateTime.setHours(values.startTime.getHours())
    startDateTime.setMinutes(values.startTime.getMinutes())
    endDateTime.setHours(values.endTime.getHours())
    endDateTime.setMinutes(values.endTime.getMinutes())
    setStartDate(startDateTime)
    setEndDate(endDateTime)
    actions.setSubmitting(false)
    actions.setTouched({}, true)
    actions.setErrors({})
    actions.setFieldError({})
    actions.setFieldTouched({}, true, true)
  }
  return (
    <React.Fragment>
      <OuterSurface>
        <Helmet title="Nutzers" />

        <Grid
          justifyContent="space-between"
          container
          spacing={10}
          sx={{ visibility: 'transparent' }}
        >
          <Grid item>
            <HeaderTypography variant="h7" gutterBottom display="inline">
              {t('LBLEnergyOverview')}
            </HeaderTypography>
          </Grid>
        </Grid>

        <ButtonGroup>
          <StyledMenuButton
            variant={period === Periods.YESTERDAY ? 'contained' : 'outlined'}
            className={period === Periods.YESTERDAY ? 'period' : ''}
            onClick={() => handleButtonClick(Periods.YESTERDAY)}
          >
            {period === Periods.YESTERDAY && <CheckIcon />} {t('LBLYesterday')}
          </StyledMenuButton>
          <StyledMenuButton
            variant={period === Periods.YEAR ? 'contained' : 'outlined'}
            className={period === Periods.YEAR ? 'period' : ''}
            onClick={() => handleButtonClick(Periods.YEAR)}
          >
            {period === Periods.YEAR && <CheckIcon />} {t('LBLYear')}
          </StyledMenuButton>
          <StyledMenuButton
            variant={period === Periods.TIMEPERIOD ? 'contained' : 'outlined'}
            className={period === Periods.TIMEPERIOD ? 'period' : ''}
            onClick={() => handleButtonClick(Periods.TIMEPERIOD)}
          >
            {period === Periods.TIMEPERIOD && <CheckIcon />}{' '}
            {t('LBLTimePeriod')}
          </StyledMenuButton>
        </ButtonGroup>

        {/* selection of time frame */}

        {period === Periods.TIMEPERIOD && (
          <>
            <StyledDiv>
              <Box display="flex" alignItems="center" marginTop={2}>
                <Formik
                  enableReinitialize={true}
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  validateOnMount={true}
                  onSubmit={handleSubmit}
                  validateOnChange={true}
                >
                  {({ errors, touched, values, setFieldValue }) => (
                    <Form noValidate>
                      <>
                        <Grid container spacing={18} alignItems="center">
                          <Grid item xs={12} sm={6} md={5} lg={4}>
                            <StyledLabel variant="h5">
                              {t('LBLStartDate')}
                            </StyledLabel>
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <LocalizationProvider
                                  dateAdapter={AdapterDayjs}
                                  adapterLocale="de"
                                >
                                  <DatePicker
                                    id={'startDate'}
                                    value={values.startDate}
                                    maxDate={dayjs()}
                                    onChange={(newValue) => {
                                      setFieldValue(
                                        'startDate',
                                        newValue
                                          ? format(
                                              newValue.toDate(),
                                              'yyyy-MM-dd'
                                            )
                                          : ''
                                      )
                                    }}
                                    renderInput={(params) => (
                                      <StyledTextField
                                        {...params}
                                        required
                                        name="startDate"
                                        margin="none"
                                        autoComplete="off"
                                        helperText={
                                          touched.startDate &&
                                          t(errors.startDate)
                                        }
                                        error={
                                          t(errors.startDate) &&
                                          touched.startDate
                                        }
                                      />
                                    )}
                                  />
                                </LocalizationProvider>
                              </Grid>
                              <Grid item xs={6}>
                                <LocalizationProvider
                                  dateAdapter={AdapterDayjs}
                                  adapterLocale="de"
                                >
                                  <TimePicker
                                    PaperProps={{
                                      sx: {
                                        boxShadow: shadows[3],
                                      },
                                    }}
                                    value={values.startTime}
                                    id={'startTime'}
                                    minutesStep={15}
                                    onChange={(newValue) => {
                                      setFieldValue(
                                        'startTime',
                                        newValue.toDate()
                                      )
                                    }}
                                    renderInput={(params) => (
                                      <StyledTimePicker
                                        {...params}
                                        name={'startTime'}
                                        margin="none"
                                        autoComplete="off"
                                        helperText={
                                          touched.startTime &&
                                          t(errors.startTime)
                                        }
                                        error={
                                          t(errors.startTime) &&
                                          touched.startTime
                                        }
                                      />
                                    )}
                                  />
                                </LocalizationProvider>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <StyledLabel variant="h5">
                              {t('LBLEndDate')}
                            </StyledLabel>
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <LocalizationProvider
                                  dateAdapter={AdapterDayjs}
                                  adapterLocale="de"
                                >
                                  <DatePicker
                                    id={'endDate'}
                                    value={values.endDate}
                                    maxDate={dayjs()}
                                    onChange={(newValue) => {
                                      setFieldValue(
                                        'endDate',
                                        newValue
                                          ? format(
                                              newValue.toDate(),
                                              'yyyy-MM-dd'
                                            )
                                          : ''
                                      )
                                    }}
                                    renderInput={(params) => (
                                      <StyledTextField
                                        {...params}
                                        required
                                        name="endDate"
                                        margin="none"
                                        autoComplete="off"
                                        helperText={
                                          touched.endDate && t(errors.endDate)
                                        }
                                        error={
                                          t(errors.endDate) && touched.endDate
                                        }
                                      />
                                    )}
                                  />
                                </LocalizationProvider>
                              </Grid>
                              <Grid item xs={6}>
                                <LocalizationProvider
                                  dateAdapter={AdapterDayjs}
                                  adapterLocale="de"
                                >
                                  <TimePicker
                                    value={values.endTime}
                                    id={'endTime'}
                                    minutesStep={15}
                                    onChange={(newValue) => {
                                      setFieldValue(
                                        'endTime',
                                        newValue.toDate()
                                      )
                                    }}
                                    renderInput={(params) => (
                                      <StyledTimePicker
                                        {...params}
                                        name={'endTime'}
                                        margin="none"
                                        autoComplete="off"
                                        helperText={
                                          touched.endTime && t(errors.endTime)
                                        }
                                        error={
                                          t(errors.endTime) && touched.endTime
                                        }
                                      />
                                    )}
                                  />
                                </LocalizationProvider>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid
                            container
                            item
                            xs={12}
                            sm={12}
                            md={4}
                            spacing={2}
                            sx={{
                              marginTop: 8,
                              paddingTop: 8,
                            }}
                          >
                            <Grid item>
                              <StyledButton
                                variant="contained"
                                color="primary"
                                type="submit"
                              >
                                <StyledText variant="h5">
                                  {t('LBLSubmit')}
                                </StyledText>
                              </StyledButton>
                            </Grid>
                            <Grid item>
                              <StyledButton
                                sx={{
                                  display: 'none',
                                }}
                              >
                                <StyledText variant="h5">
                                  <FileDownloadIcon
                                    sx={{
                                      verticalAlign: 'middle',
                                    }}
                                  />{' '}
                                  Export to .xls
                                </StyledText>
                              </StyledButton>
                            </Grid>
                          </Grid>
                        </Grid>
                      </>
                    </Form>
                  )}
                </Formik>
              </Box>
            </StyledDiv>
          </>
        )}

        {/* ENERGY KPIs BOXES STARTS HERE */}
        <HeaderTypography variant="h7">
          {t('LBLEnergyInputOutput')}
        </HeaderTypography>
        <Container>
          <EnergySummaryBox>
            <Box>
              <BoxTypography variant="h5">
                {t('LBLTotalLocalProduction')}
              </BoxTypography>
              <BoxData variant="h1">
                {processedData !== null &&
                processedData?.data?.totalEnergyProduction > 0
                  ? formatDecimal(processedData?.data?.totalEnergyProduction)
                  : '0,00'}
              </BoxData>
            </Box>
          </EnergySummaryBox>
          <EnergySummaryBox>
            <Box>
              <BoxTypography variant="h5">{t('LBLTotalDemand')}</BoxTypography>
              <BoxData variant="h1">
                {processedData !== null && processedData?.data?.totalDemand > 0
                  ? formatDecimal(processedData?.data?.totalDemand)
                  : '0,00'}
              </BoxData>
            </Box>
          </EnergySummaryBox>
          <EnergySummaryBox>
            <Box>
              <BoxTypography variant="h5">
                {t('LBLTotalGridEnergy')}
              </BoxTypography>
              <BoxData variant="h1">
                {processedData !== null &&
                processedData?.data?.totalGridEnergy > 0
                  ? formatDecimal(processedData?.data?.totalGridEnergy)
                  : '0,00'}
              </BoxData>
            </Box>
          </EnergySummaryBox>
        </Container>

        {/* The KPIs to show each energy source production share */}
        <HeaderTypography variant="h7">
          {t('LBLLocalEnergyShare')}
        </HeaderTypography>
        <Container>
          <BaseEnergyShareKpi backgroundColor={theme.tokens.tertiaryFixed}>
            <Box>
              <EnergyShareKpiTypography variant="h5">
                {t('LBLBiomassEnergy')}
              </EnergyShareKpiTypography>
              <EnergyShareKpiData variant="h1">
                {processedData !== null &&
                processedData?.data?.totalProductionByEnergySources?.BIO_MASS >
                  0
                  ? formatDecimal(
                      processedData?.data?.totalProductionByEnergySources
                        ?.BIO_MASS
                    )
                  : '0,00'}
              </EnergyShareKpiData>
            </Box>
            <IconWrapper>
              <BioMassIcon style={{ fontSize: 44 }} />
            </IconWrapper>
          </BaseEnergyShareKpi>

          <BaseEnergyShareKpi
            backgroundColor={theme.tokens.customColorSignalContainer}
          >
            <Box>
              <EnergyShareKpiTypography variant="h5">
                {t('LBLSolarEnergy')}
              </EnergyShareKpiTypography>
              <EnergyShareKpiData variant="h1">
                {processedData !== null &&
                processedData?.data?.totalProductionByEnergySources
                  ?.SOLAR_POWER > 0
                  ? formatDecimal(
                      processedData?.data?.totalProductionByEnergySources
                        ?.SOLAR_POWER
                    )
                  : '0,00'}
              </EnergyShareKpiData>
            </Box>
            <IconWrapper>
              <SolarEnergyIcon style={{ fontSize: 42 }} />
            </IconWrapper>
          </BaseEnergyShareKpi>

          <BaseEnergyShareKpi backgroundColor={theme.tokens.secondary}>
            <Box>
              <EnergyShareKpiTypography
                color={theme.tokens.surfaceBright}
                variant="h5"
              >
                {t('LBLWindEnergykwh')}
              </EnergyShareKpiTypography>
              <EnergyShareKpiData
                color={theme.tokens.surfaceBright}
                variant="h1"
              >
                {processedData !== null &&
                processedData?.data?.totalProductionByEnergySources
                  ?.WIND_ENERGY > 0
                  ? formatDecimal(
                      processedData?.data?.totalProductionByEnergySources
                        ?.WIND_ENERGY
                    )
                  : '0,00'}
              </EnergyShareKpiData>
            </Box>
            <IconWrapper>
              <WindEnergyIcon style={{ fontSize: 44 }} viewBox="0 0 32 32" />
            </IconWrapper>
          </BaseEnergyShareKpi>

          <BaseEnergyShareKpi backgroundColor={theme.tokens.secondaryFixed}>
            <Box>
              <EnergyShareKpiTypography variant="h5">
                {t('LBLHydroEnergy')}
              </EnergyShareKpiTypography>
              <EnergyShareKpiData variant="h1">
                {processedData !== null &&
                processedData?.data?.totalProductionByEnergySources
                  ?.HYDRO_POWER > 0
                  ? formatDecimal(
                      processedData?.data?.totalProductionByEnergySources
                        ?.HYDRO_POWER
                    )
                  : '0,00'}
              </EnergyShareKpiData>
            </Box>
            <IconWrapper>
              <HydroEnergyIcon style={{ fontSize: 44 }} viewBox="0 0 32 32" />
            </IconWrapper>
          </BaseEnergyShareKpi>
        </Container>
        {/* This is the button on dashboard to move directl to the Energy Mix page. */}
        <EnergyMixButton onClick={handleClick}>
          <Typography variant="h5" sx={{ color: theme.tokens.primary }}>
            Energie-Mix Details ansehen
          </Typography>
        </EnergyMixButton>

        {/* ENERGY GRAPH AND RELATED COMPONENTS */}
        {salePurchaseData?.data?.salesPurchasesDtoset?.length > 0 ? (
          <EnergyBarChart
            graphData={salePurchaseData}
            timePeriod={period}
            startDate={startDate}
            endDate={endDate}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '300px',
              border: '1px solid rgba(0, 0, 0, 0.12)',
            }}
          >
            <Typography variant="h5">{t('LBLNoDataAvailable')}</Typography>
          </div>
        )}
        {/* selection of YEAR or YESTERDAY  To show in table under the bar graph on dashboard page*/}

        {/* {(period === 'YEAR' || period === 'YESTERDAY') && (
                    <>
                    <StyledDiv>
                    
                        <Box display="flex" alignItems="center" marginTop={2}>
                        
                            <Grid container spacing={30} alignItems="center">
                            <Grid item xs={12} sm={6} md={4}>
                                <StyledButton fullWidth sx={{display:"none"}}>
                                <StyledText variant="h5" align="left">
                                    <FileDownloadIcon sx={{ verticalAlign: 'middle' }} /> Export to .xls
                                </StyledText>
                                </StyledButton>
                            </Grid>
                            </Grid>
                        </Box>
                    
                    </StyledDiv>
                    </>
                        )
                    } */}
      </OuterSurface>

      {/* Error messages */}
      <Feedback
        open={error}
        title={t('LBLDataInfo')}
        message={
          <>
            <Typography align="center">{t('LBLDataMsg')}</Typography>
          </>
        }
        handleClose={closeFeedback}
      />
    </React.Fragment>
  )
}
export default EnergyKpi
